// Shared "required field: timezone" prompt (Phase 3, Piece C — spec §7).
//
// One component, reused wherever a source's timestamps carry no zone:
//   - a generic CSV with naive timestamps,
//   - an OFW message CSV (always naive),
//   - a cover-less OFW PDF (the hotfix's assumed-tz path).
//
// Plain copy explains WHY a zone is needed (the export carries none) — no
// jargon. A sensible default is pre-selected and changeable. The caller
// solicits this ONCE per case and remembers it (so later imports don't re-ask).
//
// Flat cloud-wizard chrome consistent with FormatConfirmStep: a step title +
// lede, a mono label, and the only bordered element is the form field (the
// zone <select>). The Continue button uses the shared --primary token (dims
// legibly when disabled).

// US zones first (geographic order), then the common-overseas set. A pro-se
// user reads "Pacific Time", not "America/Los_Angeles", so each carries a
// plain label + anchor city. The IANA id is the value stored on the case.
export interface ZoneChoice {
  iana: string;
  label: string;
  city: string;
}

export const TZ_PROMPT_ZONES: ZoneChoice[] = [
  { iana: "America/New_York", label: "Eastern Time", city: "New York" },
  { iana: "America/Chicago", label: "Central Time", city: "Chicago" },
  { iana: "America/Denver", label: "Mountain Time", city: "Denver" },
  { iana: "America/Phoenix", label: "Mountain Time (no DST)", city: "Phoenix" },
  { iana: "America/Los_Angeles", label: "Pacific Time", city: "Los Angeles" },
  { iana: "America/Anchorage", label: "Alaska Time", city: "Anchorage" },
  { iana: "Pacific/Honolulu", label: "Hawaii Time", city: "Honolulu" },
  { iana: "America/Toronto", label: "Eastern Time (Canada)", city: "Toronto" },
  { iana: "America/Vancouver", label: "Pacific Time (Canada)", city: "Vancouver" },
  { iana: "Europe/London", label: "UK Time", city: "London" },
  { iana: "Europe/Paris", label: "Central European Time", city: "Paris" },
  { iana: "Australia/Sydney", label: "Eastern Australia Time", city: "Sydney" },
  { iana: "UTC", label: "Coordinated Universal Time", city: "UTC" },
];

/** A best-guess default zone — the viewer's local zone if it's in the curated
 *  list, else Eastern. Always user-changeable. */
export function defaultZone(): string {
  try {
    const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (local && TZ_PROMPT_ZONES.some((z) => z.iana === local)) return local;
  } catch {
    /* fall through */
  }
  return "America/New_York";
}

export interface TimeZonePromptProps {
  /** The currently-selected IANA zone (controlled). */
  selected: string;
  onSelect: (iana: string) => void;
  onContinue: (iana: string) => void;
  onBack?: () => void;
  /** Optional one-line context, e.g. the file name being imported. */
  filename?: string;
}

export function TimeZonePrompt(props: TimeZonePromptProps) {
  const { selected, filename } = props;
  return (
    <div className="tz-prompt">
      <h1 className="step-title">Which time zone were these messages sent in?</h1>
      <p className="step-lede">
        This export doesn't say what time zone its timestamps are in. Pick the
        zone the messages were sent in so the dates and times line up correctly.
        You can change this later.
      </p>

      {filename && (
        <div className="tz-prompt-field">
          <div className="tz-prompt-label">File</div>
          <div className="tz-prompt-filename">{filename}</div>
        </div>
      )}

      <div className="tz-prompt-field tz-prompt-field-divided">
        <div className="tz-prompt-label">Time zone</div>
        <select
          className="tz-prompt-select"
          aria-label="Time zone"
          value={selected}
          onChange={(e) => props.onSelect(e.target.value)}
        >
          {TZ_PROMPT_ZONES.map((z) => (
            <option key={z.iana} value={z.iana}>
              {z.label} — {z.city}
            </option>
          ))}
        </select>
      </div>

      <div className="wiz-cta">
        {props.onBack && (
          <button type="button" className="ghost" onClick={props.onBack}>
            ← Choose a different file
          </button>
        )}
        <button
          type="button"
          className="primary"
          disabled={!selected}
          onClick={() => selected && props.onContinue(selected)}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
